import { Api, IWord, IUser } from "@model";
import { SearchBar, ISearchQuery } from "@components";
import {
  Table,
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Tooltip,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { ColumnsType } from "antd/lib/table";
import { useSearchParams } from "react-router-dom";
import {
  ORIGIN_LANGUAGES,
  TARGET_LANGUAGE_OPTIONS,
} from "../Character/characterLanguages";

const WordListPage = () => {
  const [words, setWords] = useState<IWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<ISearchQuery>({
    q: searchParams.get("q") ?? "",
  });

  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingWord, setEditingWord] = useState<IWord | null>(null);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    const qParam = searchParams.get("q");
    if (qParam) {
      setSearchQuery({ q: qParam });
    }
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    Api.get(`/api/admin/panel/words`, {
      params: {
        word: searchQuery.q,
        translate: searchQuery.q,
        page: currentPage,
        limit: pageSize,
      },
    })
      .then((response) => {
        setWords(response.data.data);
        setLoading(false);

        const currentDataCount = response.data.data.length;
        if (currentDataCount < pageSize) {
          setTotalPages(currentPage);
        } else {
          setTotalPages(currentPage + 1);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        message.error("Failed to fetch words");
        setLoading(false);
      });
  }, [searchQuery.q, currentPage, pageSize]);

  const navigate = useNavigate();

  const handleEdit = useCallback(
    (word: IWord) => {
      setEditingWord(word);
      editForm.setFieldsValue({
        word: word.word,
        translate: word.translate,
        fromLang: word.fromLang,
        toLang: word.toLang,
      });
      setEditModalVisible(true);
    },
    [editForm],
  );

  const handleSaveEdit = async () => {
    if (!editingWord) return;

    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      await Api.put(`/api/admin/panel/words/${editingWord._id}`, {
        word: values.word,
        translate: values.translate,
        fromLang: values.fromLang,
        toLang: values.toLang,
      });
      message.success("Word updated successfully");
      setEditModalVisible(false);
      setEditingWord(null);
      editForm.resetFields();

      setLoading(true);
      Api.get(`/api/admin/panel/words`, {
        params: {
          word: searchQuery.q,
          translate: searchQuery.q,
          page: currentPage,
          limit: pageSize,
        },
      })
        .then((response) => {
          setWords(response.data.data);
          setLoading(false);

          const currentDataCount = response.data.data.length;
          if (currentDataCount < pageSize) {
            setTotalPages(currentPage);
          } else {
            setTotalPages(currentPage + 1);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          setLoading(false);
        });
    } catch (error: any) {
      console.error("Error updating word:", error);
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || "Failed to update word");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = useCallback(
    async (word: IWord) => {
      try {
        await Api.delete(`/api/admin/panel/words/${word._id}`);
        message.success("Word deleted successfully");
        setLoading(true);
        Api.get(`/api/admin/panel/words`, {
          params: {
            word: searchQuery.q,
            translate: searchQuery.q,
            page: currentPage,
            limit: pageSize,
          },
        })
          .then((response) => {
            setWords(response.data.data);
            setLoading(false);

            const currentDataCount = response.data.data.length;
            if (currentDataCount < pageSize) {
              setTotalPages(currentPage);
            } else {
              setTotalPages(currentPage + 1);
            }
          })
          .catch((error) => {
            console.error("Error:", error);
            setLoading(false);
          });
      } catch (error) {
        console.error("Error deleting word:", error);
        message.error("Failed to delete word");
      }
    },
    [searchQuery.q, currentPage, pageSize],
  );

  const getLanguageCode = (lang: string | undefined): string => {
    if (!lang) return "-";
    return lang.split("-")[0];
  };

  const getUserName = (userID: string | IUser): string => {
    if (typeof userID === "string") {
      return userID;
    }
    return userID?.name || userID?.email || userID?._id || "-";
  };

  const getUserLink = (userID: string | IUser): string | null => {
    if (typeof userID === "string") {
      return `/panel/users/edit/${userID}`;
    }
    return userID?._id ? `/panel/users/edit/${userID._id}` : null;
  };

  const formatDate = (date: any): string => {
    if (!date) return "-";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  const columns = useMemo(
    (): ColumnsType<IWord> =>
      [
        {
          title: (
            <Tooltip title="Word">
              <span>Word</span>
            </Tooltip>
          ),
          dataIndex: "word",
          key: "word",
          width: 150,
        },
        {
          title: (
            <Tooltip title="Translate">
              <span>Translate</span>
            </Tooltip>
          ),
          dataIndex: "translate",
          key: "translate",
          width: 150,
        },
        {
          title: (
            <Tooltip title="Language">
              <span>Language</span>
            </Tooltip>
          ),
          key: "language",
          width: 120,
          render: (_: any, row: IWord) => {
            const from = getLanguageCode(row.fromLang);
            const to = getLanguageCode(row.toLang);
            return (
              <div>
                <span>
                  {from} → {to}
                </span>
              </div>
            );
          },
        },
        {
          title: (
            <Tooltip title="Creator">
              <span>Creator</span>
            </Tooltip>
          ),
          dataIndex: "userID",
          key: "userID",
          width: 200,
          render: (userID: string | IUser) => {
            const link = getUserLink(userID);
            const name = getUserName(userID);
            if (link) {
              return (
                <a
                  href={link}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(link);
                  }}
                  style={{ color: "#1890ff" }}
                >
                  {name}
                </a>
              );
            }
            return <span>{name}</span>;
          },
        },
        {
          title: (
            <Tooltip title="Created At">
              <span>Created At</span>
            </Tooltip>
          ),
          dataIndex: "createdAt",
          key: "createdAt",
          width: 150,
          render: (date: any) => formatDate(date),
        },
        {
          title: "Actions",
          width: 120,
          key: "actions",
          render: (_: any, record: IWord) => (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
              >
                Edit
              </Button>
              <Popconfirm
                title={`Are you sure you want to delete "${record.word}"?`}
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
                okType="danger"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                >
                  Delete
                </Button>
              </Popconfirm>
            </div>
          ),
        },
      ].filter((a) => a.key),
    [navigate, handleEdit, handleDelete],
  );

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    if (pageSize) setPageSize(pageSize);
  };

  return (
    <>
      <div>
        <div className="flex justify-space-between">
          <SearchBar
            style={{ width: "60%", margin: "3px 0" }}
            value={searchQuery.q}
            searchFields={["word", "translate"]}
            onChange={(e) => {
              setSearchQuery(e);
              setLoading(true);
            }}
            searchType="Enter"
          />
        </div>
        <Table
          loading={loading}
          size="small"
          pagination={{
            current: currentPage,
            pageSize,
            total: totalPages * pageSize,
            onChange: handlePaginationChange,
          }}
          columns={columns}
          dataSource={words}
          rowKey="_id"
        />
      </div>

      <Modal
        title="Edit Word"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingWord(null);
          editForm.resetFields();
        }}
        onOk={handleSaveEdit}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={editLoading}
        width={500}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="word"
            label="Word"
            rules={[{ required: true, message: "Please enter word" }]}
          >
            <Input placeholder="Enter word" />
          </Form.Item>
          <Form.Item
            name="translate"
            label="Translate"
            rules={[{ required: true, message: "Please enter translation" }]}
          >
            <Input placeholder="Enter translation" />
          </Form.Item>
          <Form.Item
            name="fromLang"
            label="From Language"
            rules={[{ required: true, message: "Please select from language" }]}
          >
            <Select placeholder="Select from language">
              {ORIGIN_LANGUAGES.map((lang) => (
                <Select.Option key={lang.voiceCode} value={lang.voiceCode}>
                  {lang.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="toLang"
            label="To Language"
            rules={[{ required: true, message: "Please select to language" }]}
          >
            <Select
              placeholder="Select to language"
              options={TARGET_LANGUAGE_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default WordListPage;
